import { useCallback, useEffect, useState } from "react";
import type { BranchDto, TreasuryInput, TreasuryRecordDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";
import { errorText, fieldErrorsOf } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { ListView, type Column } from "../../components/ListView";
import { Confirm, Field, Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { LockedButton, lockTag } from "./common";
import { AccName, accountPairs, AccountSelect, accText, useAccountOptions } from "../accounting/common";
import { dec, pct } from "../items/common";

// Settings › Treasury, ported from the prototype (erp-settings.js "/settings/treasury"). Audited columns: treasury
// name, account number, kind, IBAN, account index, branch, commission account and commission tax type. The
// account index is optional; commission, its account and tax type apply to bank treasuries only, and a commission
// above zero needs an account (prototype rule, checked on the server too). Nothing is calculated from them yet.

function TreasuryForm({ tr, branches, defaultBranch, onClose, onSaved }: { tr: TreasuryRecordDto | null; branches: BranchDto[]; defaultBranch: string; onClose(): void; onSaved(x: TreasuryRecordDto, created: boolean): void }) {
  const { t, L } = useLang();
  const { opts, failed } = useAccountOptions();
  const [v, setV] = useState<TreasuryInput>(() => tr
    ? { nameAr: tr.nameAr, nameEn: tr.nameEn, branchId: tr.branchId, kind: tr.kind, accountNumber: tr.accountNumber, iban: tr.iban, accountId: tr.accountId,
        commission: dec(tr.commission, 0), commissionAccountId: tr.commissionAccountId, commissionTaxTypeId: tr.commissionTaxTypeId }
    : { nameAr: "", nameEn: "", branchId: defaultBranch, kind: "cash", accountNumber: "", iban: "", accountId: null, commission: "0", commissionAccountId: null, commissionTaxTypeId: null });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof TreasuryInput>(k: K, x: TreasuryInput[K]) => { setV((o) => ({ ...o, [k]: x })); setErrs((e) => ({ ...e, [k]: "" })); setAlert(null); };
  const bank = v.kind === "bank";
  async function save() {
    const e: Record<string, string> = {};
    if (!v.nameAr.trim()) e.nameAr = t("requiredField");
    if (!v.nameEn.trim()) e.nameEn = t("requiredField");
    if (!v.branchId) e.branchId = t("requiredField");
    const iban = v.iban.replace(/\s/g, "").toUpperCase();
    if (bank && iban && !/^SA\d{22}$/.test(iban)) e.iban = t("ibanFormat");
    const commission = String(v.commission ?? "").trim() || "0";
    if (bank && (!/^\d{1,3}(\.\d{1,2})?$/.test(commission) || Number(commission) > 100)) e.commission = t("percentRange");
    else if (bank && Number(commission) > 0 && !v.commissionAccountId) e.commissionAccountId = t("commissionAccountReq");
    setErrs(e);
    if (Object.keys(e).length) { setAlert(t("fixRequired")); return; }
    setBusy(true); setAlert(null);
    try {
      // A cash treasury, or a bank one without commission, keeps no commission account or tax type (server rule too).
      const charged = bank && Number(commission) > 0;
      const body = { ...v, accountNumber: bank ? v.accountNumber : "", iban: bank ? iban : "", commission: bank ? commission : "0",
        commissionAccountId: charged ? v.commissionAccountId : null, commissionTaxTypeId: charged ? v.commissionTaxTypeId : null };
      onSaved(tr ? await api<TreasuryRecordDto>("PUT", `/settings/treasuries/${tr.id}`, body) : await api<TreasuryRecordDto>("POST", "/settings/treasuries", body), !tr);
    } catch (err) { setErrs(fieldErrorsOf(t, err)); setAlert(errorText(t, err)); }
    finally { setBusy(false); }
  }
  return (
    <Modal title={t(tr ? "editTreasury" : "newTreasury")} wide onClose={onClose} onSubmit={save} alert={alert} busy={busy}>
      <Field label={t("nameAr")} req error={errs.nameAr} htmlFor="ff-nameAr"><input id="ff-nameAr" className="control" value={v.nameAr} onChange={(e) => set("nameAr", e.target.value)} autoComplete="off" /></Field>
      <Field label={t("nameEn")} req error={errs.nameEn} htmlFor="ff-nameEn"><input id="ff-nameEn" className="control" value={v.nameEn} onChange={(e) => set("nameEn", e.target.value)} autoComplete="off" /></Field>
      <Field label={t("branch")} req error={errs.branchId} htmlFor="ff-branch">
        <select id="ff-branch" className="control" value={v.branchId} onChange={(e) => set("branchId", e.target.value)}>
          <option value="">{t("choose")}</option>{branches.map((b) => <option key={b.id} value={b.id}>{L(b)}</option>)}
        </select>
      </Field>
      <Field label={t("accountIndex")} error={errs.accountId} htmlFor="ff-acc"><AccountSelect id="ff-acc" value={v.accountId ?? ""} onChange={(x) => set("accountId", x || null)} opts={opts} failed={failed} empty={t("noAccount")} search /></Field>
      <Field label={t("kind")} req full error={errs.kind}>
        <div className="segmented" role="radiogroup" aria-label={t("kind")}>
          {(["cash", "bank"] as const).map((k) => <label key={k}><input type="radio" name="ff-kind" value={k} checked={v.kind === k} onChange={() => set("kind", k)} /><span>{t(k)}</span></label>)}
        </div>
      </Field>
      {bank && <>
        <div className="fsec full">{t("bankDetails")}</div>
        <Field label={t("accountNumber")} error={errs.accountNumber} htmlFor="ff-accno"><input id="ff-accno" className="control num" style={{ textAlign: "start" }} dir="ltr" value={v.accountNumber} onChange={(e) => set("accountNumber", e.target.value)} autoComplete="off" /></Field>
        <Field label={t("ibanF")} error={errs.iban} htmlFor="ff-iban"><input id="ff-iban" className="control num" style={{ textAlign: "start" }} dir="ltr" placeholder="SA…" value={v.iban} onChange={(e) => set("iban", e.target.value)} autoComplete="off" /></Field>
        <Field label={t("commissionPct")} error={errs.commission} hint={t("commissionHint")} htmlFor="ff-comm"><input id="ff-comm" className="control num" style={{ textAlign: "start" }} dir="ltr" inputMode="decimal" value={String(v.commission ?? "")} onChange={(e) => set("commission", e.target.value)} autoComplete="off" /></Field>
        <Field label={t("commissionAccount")} error={errs.commissionAccountId} htmlFor="ff-cacc"><AccountSelect id="ff-cacc" value={v.commissionAccountId ?? ""} onChange={(x) => set("commissionAccountId", x || null)} opts={opts} failed={failed} empty={t("noAccount")} /></Field>
        <Field label={t("commissionTaxType")} full error={errs.commissionTaxTypeId} htmlFor="ff-ctax">
          <select id="ff-ctax" className="control" value={v.commissionTaxTypeId ?? ""} disabled={failed} onChange={(e) => set("commissionTaxTypeId", e.target.value || null)}>
            <option value="">{t("noTaxType")}</option>{opts?.taxTypes.map((x) => <option key={x.id} value={x.id}>{L(x)} ({pct(x.percentage)})</option>)}
          </select>
        </Field>
      </>}
    </Modal>
  );
}

export function Treasuries() {
  const { t, L } = useLang();
  const s = useSession();
  const toast = useToast();
  const [rows, setRows] = useState<TreasuryRecordDto[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<TreasuryRecordDto | "new" | null>(null);
  const [deleting, setDeleting] = useState<TreasuryRecordDto | null>(null);
  const accounts = useAccountOptions();
  const branch = filters.branch || (s.branch !== "all" ? s.branch : "");

  const load = useCallback(async () => {
    setFailed(null);
    const p = new URLSearchParams();
    if (filters.name) p.set("q", filters.name);
    if (filters.kind) p.set("kind", filters.kind);
    if (branch) p.set("branchId", branch);
    if (filters.acc) p.set("accountId", filters.acc);
    if (filters.cacc) p.set("commissionAccountId", filters.cacc);
    try { setRows(await api<TreasuryRecordDto[]>("GET", `/settings/treasuries${p.size ? "?" + p : ""}`)); }
    catch (e) { setFailed(errorText(t, e)); }
  }, [filters.name, filters.kind, filters.acc, filters.cacc, branch, t]);
  useEffect(() => { void load(); }, [load]);

  async function doDelete(x: TreasuryRecordDto) {
    setDeleting(null);
    try { await api("DELETE", `/settings/treasuries/${x.id}`); toast(t("deletedOk", { x: L(x) })); await load(); }
    catch (e) { toast(errorText(t, e)); }
  }
  const brName = (x: { branchNameEn: string; branchNameAr: string }) => L({ nameEn: x.branchNameEn, nameAr: x.branchNameAr });
  const columns: Column<TreasuryRecordDto>[] = [
    { k: "name", label: t("treasuryName"), cell: (x) => <span className="cell-name"><Icon name={x.kind === "bank" ? "bank" : "cash"} /> {L(x)}</span>, csv: (x) => L(x) },
    { k: "accNo", label: t("accountNumber"), cell: (x) => <span className="num">{x.accountNumber || "–"}</span>, csv: (x) => x.accountNumber },
    { k: "kind", label: t("kind"), cell: (x) => <span className={`pill ${x.kind === "bank" ? "info" : ""}`}>{t(x.kind)}</span>, csv: (x) => x.kind },
    { k: "iban", label: t("ibanF"), cell: (x) => <span className="num trunc" title={x.iban}>{x.iban || "–"}</span>, csv: (x) => x.iban },
    { k: "acc", label: t("accountIndex"), cell: (x) => <AccName a={x.account} />, csv: (x) => accText(L, x.account) },
    { k: "branch", label: t("branch"), cell: (x) => brName(x), csv: (x) => brName(x) },
    { k: "cAcc", label: t("commissionAccount"), cell: (x) => x.commissionAccount ? <><AccName a={x.commissionAccount} /> <span className="muted num">{pct(x.commission)}</span></> : <span className="muted">–</span>, csv: (x) => accText(L, x.commissionAccount) },
    { k: "cTax", label: t("commissionTaxType"), cell: (x) => (x.commissionTaxType ? L(x.commissionTaxType) : <span className="muted">–</span>), csv: (x) => L(x.commissionTaxType) }
  ];
  const newBtn = s.can("journalAdd") ? <button className="btn btn-primary" onClick={() => setEditing("new")}><Icon name="plus" />{t("newTreasury")}</button>
    : <LockedButton label={t("newTreasury")} title={t("noPermissionFor", { p: "journalAdd" })} />;

  return (
    <Shell navKey="/settings/treasury">
      <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("setTreasury")]]} title={t("settingsTreasury")} />
      <ListView rows={rows} error={failed} columns={columns} file="treasuries" testId="treasuries-table" onRefresh={() => void load()}
        search={[
          { k: "name", label: t("treasuryName"), type: "text" },
          { k: "branch", label: t("branch"), type: "select", options: s.branches.map((b) => [b.id, L(b)]) },
          { k: "kind", label: t("kind"), type: "select", options: [["cash", t("cash")], ["bank", t("bank")]] },
          { k: "acc", label: t("accountIndex"), type: "select", options: accountPairs(L, accounts.opts) },
          { k: "cacc", label: t("commissionAccount"), type: "select", options: accountPairs(L, accounts.opts) }
        ]} filters={filters} onFilters={setFilters}
        quick={(x) => `${x.nameEn} ${x.nameAr} ${x.accountNumber} ${x.iban}`} newButton={newBtn}
        empty={{ icon: "bank", title: t("emptyTitle"), body: t("emptyBody") }}
        actions={(x) => [
          { label: t("edit"), icon: "edit", run: () => setEditing(x), ...(s.can("journalUpdate") ? {} : lockTag(t)) },
          { label: "", sep: true },
          { label: t("delete"), icon: "trash", danger: true, run: () => setDeleting(x), ...(s.can("journalDelete") ? {} : lockTag(t)) }
        ]} />
      {editing && <TreasuryForm tr={editing === "new" ? null : editing} branches={s.branches} defaultBranch={s.branch !== "all" ? s.branch : s.me?.scope.branchId ?? ""}
        onClose={() => setEditing(null)} onSaved={(x, created) => { setEditing(null); toast(t(created ? "addedOk" : "updatedOk", { x: L(x) })); void load(); void s.refresh(); }} />}
      {deleting && <Confirm title={t("confirmDelete")} body={t("deleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => void doDelete(deleting)} />}
    </Shell>
  );
}
